/**
 * Onboarding state machine.
 *
 * Replaces the four-way state spread across:
 *   - localStorage.GUIDED_SETUP_KEY (boolean flag in useSmartAssistant)
 *   - OnboardingWelcomeModal.isOpen (component-local)
 *   - OnboardingChatPanel.isOpen (component-local)
 *   - GuidedOnboardingOverlay's own step counter
 *
 * Now there is one source of truth: `status`. Components subscribe and react,
 * but never own state themselves. The transitions are explicit so we can't end
 * up in two states at once (e.g., Welcome Modal + Chat Panel open simultaneously).
 *
 * Persistence: the local Zustand persist middleware keeps state across
 * refreshes. A separate hook (useSyncOnboardingState) mirrors it to
 * profiles.onboarding_state via the get/set RPCs so it survives device
 * changes and localStorage clears.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─── State shape ──────────────────────────────────────────────────────────

export type OnboardingStatus =
  | 'not_started'        // user hasn't been welcomed yet
  | 'welcome_shown'      // welcome modal visible
  | 'choosing_path'      // user clicked "Guided setup" — chat assistant guides
  | 'creating_property'  // user is on /dashboard/properties/new
  | 'creating_unit'      // user is on /dashboard/properties/:id (adding unit)
  | 'creating_booking'   // user is on /dashboard/bookings/new
  | 'completed'          // all three milestones done
  | 'dismissed';         // user explicitly opted out (we won't prompt again)

export type OnboardingPath = 'guided' | 'explore' | null;

export interface OnboardingState {
  status: OnboardingStatus;
  /** Whether the user chose guided (chat assistant active) vs explore (no prompts). */
  path: OnboardingPath;
  /** Progress derived from real DB counts; hydrated by useOnboardingProgress. */
  hasProperty: boolean;
  hasUnit: boolean;
  hasBooking: boolean;
  /** Active highlight target — used by GuidedOverlay to spotlight an element. */
  highlightTarget: string | null;
  /** Last persisted timestamp so we know if remote state is newer. */
  updatedAt: number;
  /**
   * Clerk user id this state belongs to. Persist scope is per-browser, so
   * without this we'd silently reuse the previous account's state when a
   * different user signs in on the same machine.
   */
  userId: string | null;
}

// ─── Allowed transitions ──────────────────────────────────────────────────
// Express the rules in one place. Components that try an illegal transition
// get a no-op + console.warn instead of corrupting the machine.

const ALLOWED: Record<OnboardingStatus, OnboardingStatus[]> = {
  not_started: ['welcome_shown', 'dismissed'],
  welcome_shown: ['choosing_path', 'creating_property', 'dismissed'],
  choosing_path: ['creating_property', 'creating_unit', 'creating_booking', 'dismissed'],
  creating_property: ['creating_unit', 'choosing_path', 'dismissed', 'completed'],
  creating_unit: ['creating_booking', 'choosing_path', 'dismissed', 'completed'],
  creating_booking: ['completed', 'choosing_path', 'dismissed'],
  completed: ['dismissed'], // user can dismiss the "completed" celebration
  dismissed: ['welcome_shown'], // user can opt back in later
};

function canTransition(from: OnboardingStatus, to: OnboardingStatus): boolean {
  return ALLOWED[from]?.includes(to) ?? false;
}

// ─── Actions ──────────────────────────────────────────────────────────────

export interface OnboardingActions {
  /** Set the machine to a new status; rejects illegal transitions. */
  transition: (to: OnboardingStatus, opts?: { path?: OnboardingPath }) => void;
  /** Update progress flags (typically called from useOnboardingProgress). */
  setProgress: (p: { hasProperty: boolean; hasUnit: boolean; hasBooking: boolean }) => void;
  /** Show the spotlight on a specific element (CSS selector or data-onboarding id). */
  setHighlight: (target: string | null) => void;
  /** Hydrate from server state (called once on app load). */
  hydrateFromServer: (state: Partial<OnboardingState>) => void;
  /** Convenience: dismiss from any state. */
  dismiss: () => void;
  /** Convenience: start guided flow (typically from welcome modal). */
  startGuided: () => void;
  /** Convenience: start explore flow (no prompts). */
  startExplore: () => void;
  /** Reset back to not_started — used by tests. */
  reset: () => void;
  /**
   * Adopt a clerk user id. If it differs from the currently stored userId,
   * the entire local state is reset to pristine before adopting — this
   * prevents the previous account's onboarding status from bleeding into a
   * fresh sign-in on the same browser.
   */
  setUser: (userId: string | null) => void;
}

type Store = OnboardingState & OnboardingActions;

const INITIAL: OnboardingState = {
  status: 'not_started',
  path: null,
  hasProperty: false,
  hasUnit: false,
  hasBooking: false,
  highlightTarget: null,
  updatedAt: 0,
  userId: null,
};

// ─── Store ────────────────────────────────────────────────────────────────

export const useOnboardingStore = create<Store>()(
  persist(
    (set, get) => ({
      ...INITIAL,

      transition: (to, opts) => {
        const current = get().status;
        // Allow a no-op transition to the same state (idempotent).
        if (current === to) {
          if (opts?.path !== undefined) {
            set({ path: opts.path, updatedAt: Date.now() });
          }
          return;
        }
        if (!canTransition(current, to)) {
          // eslint-disable-next-line no-console
          console.warn(`[onboarding] illegal transition: ${current} → ${to}`);
          return;
        }
        set({
          status: to,
          path: opts?.path !== undefined ? opts.path : get().path,
          // Clear highlight whenever we change major phase to avoid stale focus rings.
          highlightTarget: null,
          updatedAt: Date.now(),
        });
      },

      setProgress: (p) => {
        const prev = get();
        // No-op if nothing changed (prevents persist churn).
        if (
          prev.hasProperty === p.hasProperty &&
          prev.hasUnit === p.hasUnit &&
          prev.hasBooking === p.hasBooking
        ) {
          return;
        }
        const next: Partial<OnboardingState> = {
          hasProperty: p.hasProperty,
          hasUnit: p.hasUnit,
          hasBooking: p.hasBooking,
          updatedAt: Date.now(),
        };
        // Auto-complete: if all three milestones done and user is in any active
        // creating-* state, jump to 'completed'.
        if (
          p.hasProperty &&
          p.hasUnit &&
          p.hasBooking &&
          ['creating_property', 'creating_unit', 'creating_booking', 'choosing_path'].includes(
            prev.status
          )
        ) {
          next.status = 'completed';
        }
        set(next);
      },

      setHighlight: (target) => {
        set({ highlightTarget: target });
      },

      hydrateFromServer: (remote) => {
        // Only adopt remote values if remote is newer (avoids clobbering local
        // edits made before the network round-trip completed).
        const local = get();
        const remoteUpdated = remote.updatedAt ?? 0;
        if (remoteUpdated <= local.updatedAt) return;
        set({
          status: remote.status ?? local.status,
          path: remote.path ?? local.path,
          hasProperty: remote.hasProperty ?? local.hasProperty,
          hasUnit: remote.hasUnit ?? local.hasUnit,
          hasBooking: remote.hasBooking ?? local.hasBooking,
          updatedAt: remoteUpdated,
        });
      },

      dismiss: () => {
        // Allowed from anywhere; bypasses ALLOWED matrix for ergonomics.
        set({ status: 'dismissed', path: null, highlightTarget: null, updatedAt: Date.now() });
      },

      startGuided: () => {
        const current = get().status;
        if (current === 'completed') return; // already done, no-op
        if (canTransition(current, 'choosing_path')) {
          set({ status: 'choosing_path', path: 'guided', updatedAt: Date.now() });
        } else {
          // Always-on escape hatch: explicitly start guided even from terminal states.
          set({ status: 'choosing_path', path: 'guided', updatedAt: Date.now() });
        }
      },

      startExplore: () => {
        set({ status: 'dismissed', path: 'explore', updatedAt: Date.now() });
      },

      reset: () => {
        set({ ...INITIAL, updatedAt: Date.now() });
      },

      setUser: (userId) => {
        const current = get().userId;
        if (current === userId) return; // same user, nothing to do
        // Different user (or first sign-in after a previous account's state
        // was persisted). Wipe to pristine so they get the welcome flow.
        set({ ...INITIAL, userId, updatedAt: Date.now() });
      },
    }),
    {
      name: 'bookiapp-onboarding',
      // Persist everything except the highlight target (UI-only, ephemeral).
      partialize: (state) => ({
        status: state.status,
        path: state.path,
        hasProperty: state.hasProperty,
        hasUnit: state.hasUnit,
        hasBooking: state.hasBooking,
        updatedAt: state.updatedAt,
        userId: state.userId,
      }),
    }
  )
);

// ─── Derived selectors ────────────────────────────────────────────────────

/** True when the welcome modal should be visible. */
export const selectShouldShowWelcomeModal = (s: Store): boolean =>
  s.status === 'welcome_shown';

/** True when the chat panel is part of the guided flow (auto-open). */
export const selectShouldAutoOpenChat = (s: Store): boolean =>
  s.path === 'guided' &&
  ['choosing_path', 'creating_property', 'creating_unit', 'creating_booking'].includes(s.status);

/** True when the guided overlay should render. */
export const selectShouldShowGuidedOverlay = (s: Store): boolean =>
  s.path === 'guided' &&
  ['creating_property', 'creating_unit', 'creating_booking'].includes(s.status);

/** Progress as a 0..1 number for progress bars. */
export const selectProgress = (s: Store): number => {
  let done = 0;
  if (s.hasProperty) done += 1;
  if (s.hasUnit) done += 1;
  if (s.hasBooking) done += 1;
  return done / 3;
};

/** Current step label for UI. */
export const selectCurrentStepLabel = (s: Store): string => {
  if (s.status === 'completed') return 'completed';
  if (!s.hasProperty) return 'add_property';
  if (!s.hasUnit) return 'add_unit';
  if (!s.hasBooking) return 'create_booking';
  return 'completed';
};

// Exported for tests.
export const __test__ = { canTransition, ALLOWED, INITIAL };
